import React, { useState, useEffect } from 'react';
import { useClient } from '../../context/ClientContext';

export default function SearchTermMovers({ dateRange }) {
  const { brand, fetcher } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetcher.fetchSearchTermMovers(dateRange)
      .then(setData)
      .finally(() => setLoading(false));
  }, [dateRange]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!data) return null;

  const { rising = [], dropped = [] } = data;
  const newTerms = data.new || [];

  if (!rising.length && !newTerms.length && !dropped.length) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1"
          style={{ fontFamily: brand.typography?.headerFont }}>
        Search Term Movers
      </h3>
      <p className="text-xs text-gray-400 mb-4">Changes in search terms between periods</p>

      <div className="space-y-5">
        {/* Rising */}
        {rising.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-emerald-500 text-sm">↑</span>
              <h4 className="text-xs font-semibold text-gray-600 uppercase">Rising</h4>
            </div>
            <div className="space-y-1.5">
              {rising.slice(0, 5).map((t, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 px-3 bg-emerald-50 rounded-lg">
                  <span className="text-sm text-gray-700 truncate mr-2">{t.term}</span>
                  <span className="text-xs font-medium text-emerald-600 whitespace-nowrap">
                    +{t.delta} conv ({t.previousConversions} → {t.conversions})
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New */}
        {newTerms.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full text-xs font-bold text-white"
                    style={{ backgroundColor: brand.colors?.primary || '#2563eb' }}>N</span>
              <h4 className="text-xs font-semibold text-gray-600 uppercase">New This Period</h4>
            </div>
            <div className="space-y-1.5">
              {newTerms.slice(0, 5).map((t, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 px-3 bg-blue-50 rounded-lg">
                  <span className="text-sm text-gray-700 truncate mr-2">{t.term}</span>
                  <span className="text-xs font-medium text-gray-500 whitespace-nowrap">
                    {t.impressions.toLocaleString()} impr
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dropped */}
        {dropped.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-gray-400 text-sm">↓</span>
              <h4 className="text-xs font-semibold text-gray-600 uppercase">Dropped</h4>
            </div>
            <div className="space-y-1.5">
              {dropped.slice(0, 5).map((t, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded-lg opacity-75">
                  <span className="text-sm text-gray-500 truncate mr-2">{t.term}</span>
                  <span className="text-xs font-medium text-gray-400 whitespace-nowrap">
                    was {t.previousImpressions.toLocaleString()} impr
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
